import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Selectors
import * as OnboardingStateSelectors from 'Shared/selectors/OnboardingState.selectors';

// Actions
import { advanceOnboardingStateAction, backOnboardingStateAction } from 'Shared/actions/onboardingState';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import useWindowDimensions from 'Wpds/hooks/useWindowDimensions';

// Components
import { WpButtonGroup, WpButton } from 'Wpds/components/button';
import SuggestedTopics from './SuggestedTopics/SuggestedTopics';
import AgendaList from './AgendaList/AgendaList';
import ShareAgendaModal from '../ShareAgendaModal';
import WpDrawer from 'Wpds/components/drawer/WpDrawer';
import WpIcon from 'Wpds/components/icon/WpIcon';

// Utils
import { trackInvitedTeammate, trackSharedAgendaInOnboarding } from 'Shared/tracking/tracking';

// Styles
import './CreateMeetingAgenda.scss';

const baseClassName = 'CreateMeetingAgenda';

const SuggestTopicsMarkup = ({ isOpen, toggle }) => {
  const { width } = useWindowDimensions();

  if (width > 782) {
    return (
      <div className={`${baseClassName}__body__topics`}>
        <SuggestedTopics />
      </div>
    );
  } else {
    return (
      <WpDrawer isOpen={isOpen} onToggle={toggle}>
        <SuggestedTopics />
      </WpDrawer>
    );
  }
};

const CreateMeetingAgenda = () => {
  const [isShareModalOpen, toggleShareModalOpen] = useToggle(false);
  const [isSuggestedDrawerOpen, toggleIsSuggestedDrawerOpen] = useToggle(false);

  const [error, setError] = useState('');
  const dispatch = useDispatch();

  const recurringMeetings = useSelector(state => OnboardingStateSelectors.getRecurringMeetings(state));
  const workspaces = useSelector(state => OnboardingStateSelectors.getWorkspaces(state));
  const topics = useSelector(state => OnboardingStateSelectors.getDiscussionTopics(state));

  const teammates = {};
  workspaces.forEach(workspace => {
    const recurringMeeting = recurringMeetings.find(({ uuid }) => uuid === workspace.recurring_meeting_uuid);
    teammates[workspace.uuid.toString()] = Object.assign(
      { errorMessage: null, email: recurringMeeting?.teammateEmail || '' },
      workspace
    );
  });

  useEffect(() => {
    // reset the error if a topic is added
    if (error !== '' && topics.length > 0) setError('');
  }, [topics]);

  const checkForError = () => {
    if (topics.length === 0) {
      setError('Please add at least one agenda item.');
      return true;
    }
    return false;
  };

  const handleBackClick = () => {
    dispatch(backOnboardingStateAction());
  };

  const handleShareLater = updatedWorkspaces => {
    const hasError = checkForError();
    if (hasError) return;

    updatedWorkspaces.forEach(workspace => {
      workspace.name = workspace.name || workspace.email;
      workspace['skipInvite'] = true;
    });

    dispatch(
      advanceOnboardingStateAction({
        onboardingState: {
          workspaces: updatedWorkspaces,
        },
        didSkip: false,
      })
    );
  };

  const handleShareClick = () => {
    const hasError = checkForError();
    if (hasError) return;
    else toggleShareModalOpen();
  };

  const handleInModalShareClick = updatedWorkspaces => {
    const hasError = checkForError();
    if (hasError) return;

    updatedWorkspaces.forEach(workspace => {
      workspace.fullName = workspace.fullName || workspace.email;
    });

    trackSharedAgendaInOnboarding(updatedWorkspaces.length, topics.length);
    trackInvitedTeammate(updatedWorkspaces.length, 'Magic 1:1 onboarding');

    return dispatch(
      advanceOnboardingStateAction({
        onboardingState: {
          workspaces: updatedWorkspaces,
        },
        didSkip: false,
      })
    ).then(res => {
      if (!res.error) {
        toggleShareModalOpen();
      }
    });
  };

  return (
    <>
      <div className={baseClassName}>
        <div className={`${baseClassName}__body`}>
          <div className={`${baseClassName}__body__agenda`}>
            <AgendaList
              error={error}
              footer={
                <div className={`${baseClassName}__footer`}>
                  <div className={`${baseClassName}__footer__mobile-add-button`}>
                    <WpButton variant="tertiary" onClick={toggleIsSuggestedDrawerOpen}>
                      <WpIcon name="plus-circle" />
                      Add suggested topic
                    </WpButton>
                  </div>
                  <WpButtonGroup>
                    <WpButton onClick={handleShareClick} qaTest="share-agenda">
                      Next
                    </WpButton>
                    <WpButton variant="tertiary" onClick={handleBackClick}>
                      Back
                    </WpButton>
                  </WpButtonGroup>
                </div>
              }
            />
          </div>
          <SuggestTopicsMarkup isOpen={isSuggestedDrawerOpen} toggle={toggleIsSuggestedDrawerOpen} />
        </div>
      </div>
      <ShareAgendaModal
        isOpen={isShareModalOpen}
        toggleOpen={toggleShareModalOpen}
        handleShareAgenda={handleInModalShareClick}
        handleShareLater={handleShareLater}
        teammates={teammates}
      />
    </>
  );
};

export default CreateMeetingAgenda;
